import styled from '@emotion/styled';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import { SITE } from '@hotjar/webapp/constants/routes';
import { InProgressSessionsTeaser } from '@hotjar/webapp/features/InProgressSessionsTeaser';
import { useCurrentSite } from '@hotjar/site';
import { useInProgressSessions } from '@hotjar/webapp/hooks/useInProgressSessions';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { useSamplingState } from '@hotjar/webapp/hooks/useSamplingState';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { tokens } from '@hotjar/hotsauce';
import SPINNER_ICON from '@hotjar/shared-assets/images/icons/icon-status-running.gif';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  width: 100%;
  padding: ${tokens.spacer.x8};
`;

export const RecordingsEmptyDataState = (): JSX.Element | null => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { inDisabledState, inWarningState, isLoading } = useSamplingState();
  const { show: openTrackingCodeModal } = useModalTrackingCode();
  const { data: { count = 0 } = {} } = useInProgressSessions({
    siteId: currentSite?.id,
    selectRandom: true,
  });

  const handleWarningStateClick = () => {
    if (currentSite?.id) {
      openTrackingCodeModal(currentSite, 2, 'Recordings List Page');
    }
  };

  const handleDisabledStateClick = () => {
    if (currentSite?.id) {
      navigateTo(SITE.SETTINGS.path, { siteId: currentSite?.id });
    }
  };

  if (isLoading || !currentSite?.id) {
    return null;
  }

  if (inWarningState) {
    return (
      <Wrapper>
        <EmptyState
          icon="warning_alt"
          iconColor="text_warning"
          buttonText={t('activation:empty-states.pending-data.recordings.cta-warning')}
          onButtonClick={handleWarningStateClick}
          title={t('activation:empty-states.pending-data.recordings.title-warning')}
          subtitle={t('activation:empty-states.pending-data.recordings.subtitle-warning', {
            siteUrl: currentSite?.url,
          })}
        />
      </Wrapper>
    );
  }

  if (inDisabledState) {
    return (
      <Wrapper>
        <EmptyState
          icon="warning_alt"
          iconColor="text_warning"
          buttonText={t('activation:empty-states.pending-data.recordings.cta-disabled')}
          buttonKind="secondary"
          onButtonClick={handleDisabledStateClick}
          title={t('activation:empty-states.pending-data.recordings.title-disabled')}
          subtitle={t('activation:empty-states.pending-data.recordings.subtitle-disabled')}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {count > 0 ? (
        <InProgressSessionsTeaser siteId={currentSite.id} />
      ) : (
        <EmptyState
          img={SPINNER_ICON}
          title={t('activation:empty-states.pending-data.recordings.title')}
          subtitle={t('activation:empty-states.pending-data.recordings.subtitle')}
        />
      )}
    </Wrapper>
  );
};

export interface FeedbackEmptyDataStateProps {
  isDefaultRange?: boolean;
  resetFilters: () => void;
  changeRange: (range: string) => void;
}

export const FeedbackEmptyDataState = ({
  isDefaultRange,
  resetFilters,
  changeRange,
}: FeedbackEmptyDataStateProps): JSX.Element => {
  const { t } = useTranslation();

  const subtitle = isDefaultRange
    ? t('activation:empty-states.pending-data.feedback.subtitle')
    : t('activation:empty-states.pending-data.feedback.subtitle-filtered');
  const buttonText = isDefaultRange
    ? t('activation:empty-states.pending-data.feedback.cta')
    : t('activation:empty-states.pending-data.feedback.cta-filtered');

  const handleRangeChange = () => {
    changeRange('90');
  };

  const onClick = isDefaultRange ? handleRangeChange : resetFilters;

  return (
    <EmptyState
      kind="condensed"
      buttonText={buttonText}
      buttonKind="secondary"
      onButtonClick={onClick}
      icon="tray_empty"
      title={t('activation:empty-states.pending-data.feedback.title')}
      subtitle={subtitle}
    />
  );
};
